




























































































































import Vue from 'vue'
import {
  defineComponent, ref, onMounted, reactive, getCurrentInstance,
} from '@vue/composition-api'
import { AggregationResultAttributes, BRDisplayType } from '@/api/v2/types/aggregation-result'
import {
  fetchAggregationContainer,
  AggregationContainerDetailAttrs,
  AggregationAttrs,
  updateBusinessRuleAssignmentDisplayType,
} from '@/api/v2/report/AggregationContainer'
import {
  fetchAvailableUsers,
  UserAttrs,
  enqueueAggregationJob,
  getAggregationJobStatus,
} from '@/api/v2/report/Aggregation'
import {
  fetchAggregationUsers,
  AggregationUserAttrs,
  createAggregationUser,
  destroyAggregationUser,
} from '@/api/v2/report/AggregationUser'
import { saveBusinessRuleResult } from '@/api/v2/report/BusinessRuleResult'
import { handleApiError, ApiError } from '@/utils/errorHandler'
import { isValidDateFormat, useDateRange } from '@/views/v2-temp/common/composables/useDateRange'
import { useAggregationType } from './composables/useAggregationType'
import { DisplayMode } from '@/views/v2-temp/report/aggregation-container/composables/useAggregationResultTable'
import useUserPreferences from '@/views/v2-temp/common/composables/useUserPreferences'
import AggregationResultTable from './components/AggregationResultTable.vue'
import DateRangePicker from '@/views/v2-temp/common/components/DateRangePicker.vue'
import AggregationTableTitle from './components/AggregationTableTitle.vue'
import AggregationTableFilters from './components/AggregationTableFilters.vue'
import AggregationTableMenu from './components/AggregationTableMenu.vue'
import { useBusinessRuleResult } from './composables/useBusinessRuleResult'

export default defineComponent({
  name: 'AggregationContainerShow',
  components: {
    AggregationResultTable,
    DateRangePicker,
    AggregationTableTitle,
    AggregationTableFilters,
    AggregationTableMenu,
  },
  props: {
    id: { type: Number, required: true },
    startDate: {
      type: String,
      required: true,
      validator: (value: string) => isValidDateFormat(value),
    },
    endDate: {
      type: String,
      required: true,
      validator: (value: string) => isValidDateFormat(value),
    },
  },
  setup(props) {
    const vm = getCurrentInstance()!.proxy

    const { dateRange } = useDateRange(props.startDate, props.endDate)

    const container = ref<AggregationContainerDetailAttrs | null>(null)
    const loading = ref(false)
    const error = ref<Error | null>(null)

    type AggregationId = number
    const displayModes = reactive<Record<AggregationId, DisplayMode>>({})
    const selectedUsers = reactive<Record<AggregationId, number | null>>({})
    const availableUsers = reactive<Record<AggregationId, UserAttrs[]>>({})
    const aggregationUsers = reactive<Record<AggregationId, AggregationUserAttrs[]>>({})
    const loadings = reactive<Record<AggregationId, boolean>>({})
    const aggregations = reactive<Record<AggregationId, AggregationResultAttributes>>({})
    const jobStatuses = reactive<Record<AggregationId, string | null>>({})
    const jobTimestamps = reactive<Record<AggregationId, { createdAt: string | null, updatedAt: string | null }>>({})
    const displaySumRows = reactive<Record<AggregationId, boolean>>({})
    const hideZeroRowsMap = reactive<Record<AggregationId, boolean>>({})

    const brDisplaySettings = reactive<Record<AggregationId, Record<string, BRDisplayType>>>({})

    const { judgeAggregationType } = useAggregationType()
    const { updateAggregations } = useBusinessRuleResult(aggregations)
    const {
      preferences, setDisplayMode, setDisplaySumRow, getDisplaySumRow, getDisplayMode,
      setHideZeroRows, getHideZeroRows,
    } = useUserPreferences()

    const getInitialDisplayMode = (aggregation: AggregationAttrs) => {
      const storedMode = getDisplayMode(props.id.toString(), aggregation.id.toString())

      // UserAggregationの場合は日次などはselectedUserが選択されていない場合は許可していないので
      if (judgeAggregationType(aggregation, 'User') && (!storedMode || ['daily', 'today'].includes(storedMode))) {
        setDisplayMode(props.id.toString(), aggregation.id.toString(), 'sum')

        return 'sum'
      }

      if (storedMode) return storedMode

      setDisplayMode(props.id.toString(), aggregation.id.toString(), 'daily')

      return 'daily'
    }

    const getInitialDisplaySumRow = (aggregation: AggregationAttrs) => {
      const storedSumRow = getDisplaySumRow(props.id.toString(), aggregation.id.toString())

      if (storedSumRow) return storedSumRow

      setDisplaySumRow(props.id.toString(), aggregation.id.toString(), false)

      return false
    }

    const handleFetchAvailableUsers = async (aggregationId: number, callback: () => void) => {
      const users = await fetchAvailableUsers(props.id, aggregationId)
      availableUsers[aggregationId] = users
      callback()
    }

    const handleCreateAggregationUser = async (aggregationId: number, { userId, callback }: { userId: number, callback: () => void }) => {
      try {
        await createAggregationUser(props.id, aggregationId, userId)
        const users = await fetchAggregationUsers(props.id, aggregationId)
        aggregationUsers[aggregationId] = users
        callback()
      } catch (e) {
        handleApiError(e as ApiError, { $toast: vm.$toast })
      }
    }

    const handleDestroyAggregationUser = async (aggregationId: number, { id, userId, callback }: { id: number, userId: number, callback: () => void }) => {
      try {
        await destroyAggregationUser(props.id, aggregationId, id)
        const users = await fetchAggregationUsers(props.id, aggregationId)
        aggregationUsers[aggregationId] = users
        callback()
      } catch (e) {
        handleApiError(e as ApiError, { $toast: vm.$toast })
      }
    }

    const getAggregationJobStatusData = async (aggregationId: number) => {
      try {
        loadings[aggregationId] = true
        const res = await getAggregationJobStatus(props.id, aggregationId, {
          start_date: dateRange.value[0],
          end_date: dateRange.value[1],
        })
        const { status, result, meta } = res

        jobStatuses[aggregationId] = status

        jobTimestamps[aggregationId] = { createdAt: meta.createdAt, updatedAt: meta.updatedAt }
        if (result) {
          aggregations[aggregationId] = result
          // APIレスポンスのdisplayTypeでbrDisplaySettingsを更新
          const displaySettings: Record<string, BRDisplayType> = {}
          result.businessRules.data.forEach(br => {
            displaySettings[br.id] = br.attributes.displayType || 'sum'
          })
          Vue.set(brDisplaySettings, aggregationId, displaySettings)
        }
      } catch (err) {
        const apiError = err as ApiError
        if (apiError.response?.status === 404) {
          jobStatuses[aggregationId] = null
        } else {
          handleApiError(apiError, { $toast: vm.$toast })
        }
      } finally {
        loadings[aggregationId] = false
      }
    }

    const pollAggregationJobStatus = async (aggregationId: number) => {
      let intervalTime = 5000 // 初期間隔 5 秒
      const interval = setInterval(async () => {
        try {
          loadings[aggregationId] = true
          await getAggregationJobStatusData(aggregationId)
          if (['completed', 'failed', null].includes(jobStatuses[aggregationId])) {
            clearInterval(interval)
          }
        } catch (err) {
          const apiError = err as ApiError
          clearInterval(interval)
          handleApiError(apiError, { $toast: vm.$toast })
        } finally {
          loadings[aggregationId] = false
        }
        intervalTime = Math.min(intervalTime * 2, 60000) // 最大1分まで増加
      }, intervalTime)
    }

    const fetchAggregationData = async (aggregationId: number) => {
      let contextType
      if (judgeAggregationType(aggregations[aggregationId], 'User')) contextType = 'User' as const
      else if (judgeAggregationType(aggregations[aggregationId], 'Club')) contextType = 'Club' as const
      else if (judgeAggregationType(aggregations[aggregationId], 'TableDetail')) contextType = 'TableDetail' as const

      const params = {
        start_date: dateRange.value[0],
        end_date: dateRange.value[1],
        context_type: contextType,
        context_id: selectedUsers[aggregationId] || null,
      }

      try {
        loadings[aggregationId] = true
        await enqueueAggregationJob(props.id, aggregationId, params)
        jobStatuses[aggregationId] = 'in_progress'
        pollAggregationJobStatus(aggregationId)
      } catch (err) {
        const apiError = err as ApiError
        if (apiError.response?.status === 404) {
          jobStatuses[aggregationId] = null
        } else {
          handleApiError(apiError, { $toast: vm.$toast })
        }
      } finally {
        loadings[aggregationId] = false
      }
    }

    const handleDisplayModeChange = (aggregationId: number, mode: DisplayMode) => {
      setDisplayMode(props.id.toString(), aggregationId.toString(), mode)
      loadings[aggregationId] = true
      displayModes[aggregationId] = mode
      loadings[aggregationId] = false
    }

    const handleSelectedUserChange = (aggregationId: number, userId: number | null) => {
      loadings[aggregationId] = true
      selectedUsers[aggregationId] = userId
      loadings[aggregationId] = false
    }

    const handleBRDisplayTypeChange = async (aggregationId: number, brId: number | string, type: BRDisplayType) => {
      await updateBusinessRuleAssignmentDisplayType(props.id, aggregationId, brId, type)
      if (!brDisplaySettings[aggregationId]) {
        Vue.set(brDisplaySettings, aggregationId, {})
      }
      Vue.set(brDisplaySettings[aggregationId], brId, type)
    }

    const handleToggleSumRow = (aggregationId: number) => {
      const newValue = !displaySumRows[aggregationId]
      displaySumRows[aggregationId] = newValue
      setDisplaySumRow(props.id.toString(), aggregationId.toString(), newValue)
    }

    const handleHideZeroRowsToggle = (aggregationId: number) => {
      const newValue = !hideZeroRowsMap[aggregationId]
      hideZeroRowsMap[aggregationId] = newValue
      setHideZeroRows(props.id.toString(), aggregationId.toString(), newValue)
    }

    const handleReload = async (aggregationId: number) => {
      try {
        loadings[aggregationId] = true
        await fetchAggregationData(aggregationId)
      } catch (e) {
        handleApiError(e as ApiError, { $toast: vm.$toast })
      } finally {
        loadings[aggregationId] = false
      }
    }

    const saveResult = async ({
      aggregationId,
      businessDate,
      businessRuleId,
      contextType,
      contextId,
      resultValue,
    }: {
      aggregationId: number
      businessDate: string
      businessRuleId: number
      contextType: string
      contextId: number
      resultValue: any
    }) => {
      try {
        const results = await saveBusinessRuleResult({
          aggregationContainerId: props.id,
          aggregationId,
          businessRuleId,
          businessDate,
          contextType,
          contextId,
          resultValue,
        })
        updateAggregations(results)
      } catch (e) {
        handleApiError(e as ApiError, { $toast: vm.$toast })
      }
    }

    async function loadContainer() {
      loading.value = true
      error.value = null

      try {
        container.value = await fetchAggregationContainer(props.id)

        await Promise.all(container.value.rows.data.flatMap(row => row.attributes.cols.data.flatMap(col => col.attributes.aggregations.data.map(async agg => {
          // 表示モードを初期化
          if (!displayModes[agg.attributes.id]) {
            Vue.set(displayModes, agg.attributes.id, getInitialDisplayMode(agg.attributes))
          }

          // ローディング状態を初期化
          if (!loadings[agg.attributes.id]) {
            Vue.set(loadings, agg.attributes.id, false)
          }
          if (!jobStatuses[agg.attributes.id]) {
            Vue.set(jobStatuses, agg.attributes.id, null)
          }
          if (!jobTimestamps[agg.attributes.id]) {
            Vue.set(jobTimestamps, agg.attributes.id, { createdAt: null, updatedAt: null })
          }
          if (!displaySumRows[agg.attributes.id]) {
            Vue.set(displaySumRows, agg.attributes.id, getInitialDisplaySumRow(agg.attributes))
          }

          if (hideZeroRowsMap[agg.attributes.id] === undefined) {
            Vue.set(hideZeroRowsMap, agg.attributes.id, getHideZeroRows(props.id.toString(), agg.attributes.id.toString()))
          }

          // UserAggregationの場合の初期化
          if (judgeAggregationType(agg.attributes, 'User')) {
            if (!selectedUsers[agg.attributes.id]) {
              Vue.set(selectedUsers, agg.attributes.id, null)
            }
            if (!availableUsers[agg.attributes.id]) {
              Vue.set(availableUsers, agg.attributes.id, await fetchAvailableUsers(props.id, agg.attributes.id))
            }
            if (!aggregationUsers[agg.attributes.id]) {
              Vue.set(aggregationUsers, agg.attributes.id, await fetchAggregationUsers(props.id, agg.attributes.id))
            }
          }

          // ジョブの状態を確認し、必要に応じてデータを取得
          if (!aggregations[agg.attributes.id]) {
            Vue.set(aggregations, agg.attributes.id, null)
          }
          await getAggregationJobStatusData(agg.attributes.id)
          if (!aggregations[agg.attributes.id] || jobStatuses[agg.attributes.id] === null) {
            await fetchAggregationData(agg.attributes.id)
          }
        }))))
      } catch (e) {
        handleApiError(e as ApiError, { $toast: vm.$toast })
      } finally {
        loading.value = false
      }
    }

    const onDateRangeChange = (dates: string[]) => {
      dateRange.value = dates
      loadContainer()
    }

    onMounted(loadContainer)

    return {
      container,
      loading,
      error,
      dateRange,
      displayModes,
      selectedUsers,
      availableUsers,
      aggregationUsers,
      aggregations,
      loadings,
      jobStatuses,
      jobTimestamps,
      displaySumRows,
      hideZeroRowsMap,
      brDisplaySettings,
      onDateRangeChange,
      handleFetchAvailableUsers,
      handleCreateAggregationUser,
      handleDestroyAggregationUser,
      handleDisplayModeChange,
      handleSelectedUserChange,
      handleToggleSumRow,
      handleHideZeroRowsToggle,
      handleBRDisplayTypeChange,
      handleReload,
      saveResult,
      preferences,
    }
  },
})
